import './style/searchRankings.scss';

import React, {MouseEventHandler, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {APIType, RankPositionFilter, SearchRankingSort, StatCardDataType} from '../../util/enums';
import {
    OrderDirection,
    SearchRankingProduct,
    SearchRankingsColumnDef,
    SearchRankingStats,
    StatCardData,
    TrackedKeyword
} from '../../util/interfaces';
import {PortalSelectOption, SearchRankingMode} from '../../util/types';
import SearchRankingsControlBar from './controlBar/searchRankingsControlBar';
import PositionFilterBar from './positionFilterBar/positionFilterBar';
import SearchRankingsTable from './table/searchRankingsTable';
import ImportKeywordsDialog from './importKeywords/importKeywordsDialog';
import {AppContext} from '../misc/appContext';
import APIConnectionBanner from '../apiConnection/apiConnectionBanner';
import {OAUTH_SEARCH_RANKINGS_REDIRECT} from '../../util/constants';
import {BolmateStatCard, BolmateTooltip} from '../controls';
import classNames from 'classnames';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import InfoBanner from '../misc/infoBanner';
import {parseThousandSeparator} from '../../util/utils.ts';

interface Props {
    products: Array<SearchRankingProduct>;
    stats: SearchRankingStats;
    prevStats: SearchRankingStats;
    loadingProducts: boolean;
    loadingStats: boolean;
    onSearchChange: (value: string) => void;
    rankingMode: SearchRankingMode;
    onRankingModeChange: (mode: SearchRankingMode) => void;
    activePositionFilter: RankPositionFilter;
    onPositionFilterChange: (filter: RankPositionFilter) => void;
    selectedCountries: Array<'NL' | 'BE'>;
    onCountryChange: (value: string) => void;
    selectedAccountIDs: Array<string>;
    storeItems: Array<PortalSelectOption<string>>;
    onStoreChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    expandedProductId: string | null;
    onToggleExpand: (ean: string) => void;
    sortField: SearchRankingSort | null;
    sortOrder: OrderDirection;
    onSort: (field: SearchRankingSort) => void;
    page: number;
    limit: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    accountId: string;
    onKeywordsChanged: VoidFunction;
    columns: Array<SearchRankingsColumnDef>;
    onExportExcel: VoidFunction;
    visibleColumnFields: Array<string>;
    allColumnItems: Array<PortalSelectOption>;
    onColumnsChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    importDialogActive: boolean;
    onImportClick: VoidFunction;
    onImportClose: VoidFunction;
    onAddKeyword: (
        ean: string,
        keyword: string,
        callback: (keyword: TrackedKeyword, restored: boolean) => void
    ) => void;
    onUpgradeLimit: MouseEventHandler;
    manualKeywordsCount: number;
    manualKeywordsLimit: number;
    hasModule: boolean;
    onCloseAccountItems: VoidFunction;
}

const EMPTY_KPI: StatCardData = {
    label: '\u2014',
    value: 0,
    prevValue: 0
};

type KPICard = StatCardData & {tooltip?: string};

function computeDelta(current: number, prev: number | null | undefined): Pick<StatCardData, 'prevValue' | 'value'> {
    const noData: Pick<StatCardData, 'prevValue' | 'value'> = {
        prevValue: 0,
        value: 0
    };
    if (prev == null) return noData;
    if (current === 0 && prev === 0) return noData;
    return {value: current, prevValue: prev};
}

function isEmptyStats(s: SearchRankingStats): boolean {
    return (
        s.avg_rank === 0 &&
        s.best_rank === 0 &&
        s.total_search_volume === 0 &&
        s.visibility_score === 0 &&
        s.total_keywords === 0
    );
}

function buildKpiCards(
    stats: SearchRankingStats | null,
    prevStats: SearchRankingStats | null,
    t: (k: string, f: string) => string
): Array<KPICard | Array<KPICard>> {
    if (!stats || isEmptyStats(stats)) {
        return [
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.avgRank', 'Gem. zoekpositie'),
                alternates: [{...EMPTY_KPI, label: t('searchRankings.kpi.bestRank', 'Beste zoekpositie')}]
            },
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.keywordsTop3', 'Keywords in Top 3'),
                alternates: [{...EMPTY_KPI, label: t('searchRankings.kpi.keywordsTop10', 'Keywords in Top 10')}]
            },
            {...EMPTY_KPI, label: t('searchRankings.kpi.totalSearchVolume', 'Totaal zoekvolume')},
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.visibility', 'Zichtbaarheid'),
                valueType: StatCardDataType.PERCENTAGE,
                tooltip: t('searchRankings.kpi.visibilityTooltip', 'Percentage van producten zichtbaar in de top 10')
            },
            {...EMPTY_KPI, label: t('searchRankings.kpi.totalKeywords', 'Totaal keywords')}
        ];
    }

    const avgRankDelta = computeDelta(stats.avg_rank, prevStats?.avg_rank);
    const bestRankDelta = computeDelta(stats.best_rank, prevStats?.best_rank);
    const top3Delta = computeDelta(stats.position_counts.TOP_3, prevStats?.position_counts.TOP_3);
    const top10Delta = computeDelta(stats.position_counts.TOP_10, prevStats?.position_counts.TOP_10);
    const volumeDelta = computeDelta(stats.total_search_volume, prevStats?.total_search_volume);
    const visibilityDelta = computeDelta(stats.visibility_score, prevStats?.visibility_score);
    const keywordsDelta = computeDelta(stats.total_keywords, prevStats?.total_keywords);
    const categoriesDelta = computeDelta(stats.total_categories, prevStats?.total_categories);

    return [
        [
            {
                label: t('searchRankings.kpi.avgRank', 'Gem. zoekpositie'),
                invertTrend: true,
                ...avgRankDelta
            },
            {
                label: t('searchRankings.kpi.bestRank', 'Beste zoekpositie'),
                invertTrend: true,
                ...bestRankDelta
            }
        ],
        [
            {
                label: t('searchRankings.kpi.keywordsTop3', 'Keywords in Top 3'),
                ...top3Delta
            },
            {
                label: t('searchRankings.kpi.keywordsTop10', 'Keywords in Top 10'),
                ...top10Delta
            }
        ],
        {
            label: t('searchRankings.kpi.totalSearchVolume', 'Totaal zoekvolume'),
            truncateThreshold: 10000,
            ...volumeDelta
        },
        {
            label: t('searchRankings.kpi.visibility', 'Zichtbaarheid'),
            valueType: StatCardDataType.PERCENTAGE,
            tooltip: t('searchRankings.kpi.visibilityTooltip', 'Percentage van producten zichtbaar in de top 10'),
            ...visibilityDelta
        },
        [
            {
                label: t('searchRankings.kpi.totalKeywords', 'Totaal keywords'),
                ...keywordsDelta
            },
            {
                label: t('searchRankings.kpi.totalCategories', 'Totaal categorieën'),
                ...categoriesDelta
            }
        ]
    ];
}

export default function SearchRankings(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {
        products,
        stats,
        prevStats,
        loadingProducts,
        onSearchChange,
        rankingMode,
        onRankingModeChange,
        activePositionFilter,
        onPositionFilterChange,
        selectedCountries,
        onCountryChange,
        selectedAccountIDs,
        storeItems,
        onStoreChange,
        expandedProductId,
        onToggleExpand,
        sortField,
        sortOrder,
        onSort,
        page,
        limit,
        totalRows,
        onPageChange,
        accountId,
        onKeywordsChanged,
        columns,
        onExportExcel,
        visibleColumnFields,
        allColumnItems,
        onColumnsChange,
        importDialogActive,
        onImportClick,
        onImportClose,
        onAddKeyword,
        onUpgradeLimit,
        manualKeywordsCount,
        manualKeywordsLimit,
        hasModule,
        onCloseAccountItems
    } = props;

    const kpis = useMemo(() => buildKpiCards(stats, prevStats, t), [stats, prevStats, t]);

    function renderAPIBanner() {
        if (!account) return null;
        const apiKey = account.getActiveAPIKey();
        if (apiKey?.enabledForAPIType(APIType.ADVERTISER)) return null;
        return (
            <APIConnectionBanner
                oauthState={OAUTH_SEARCH_RANKINGS_REDIRECT}
                className='api-banner'
                title={t('searchRankings.apiBannerTitle', 'Welkom bij Search Rankings!')}
            >
                <p>
                    {t(
                        'searchRankings.apiBanner',
                        'Bekijk hier hoe hoog jouw producten in de zoekresultaten verschijnen.\nTrack de keywords van Bol en voeg zelf jouw belangrijkste keywords toe.\n\nVernieuw eerst jouw koppeling met Bol:'
                    )}
                </p>
            </APIConnectionBanner>
        );
    }

    function renderLimitIndicator() {
        const renderLimit =
            manualKeywordsLimit >= 1000 ? parseThousandSeparator(manualKeywordsLimit) : manualKeywordsLimit;
        const elem = (
            <div
                className={classNames('account-limit', !hasModule && 'upgradable')}
                onClick={hasModule ? undefined : onUpgradeLimit}
            >
                <span className='gray'>Keywords:</span>
                <span className='current-count'>{manualKeywordsCount}</span>
                <span className='gray'>/ {renderLimit}</span>
                {!hasModule && <AwesomeIcon name='circle-up' prefix='far' className='upgrade-icon gray' />}
            </div>
        );
        if (hasModule) return elem;
        return (
            <BolmateTooltip text={t('searchRankings.upgradeForHigherLimit', 'Upgrade voor hoger limiet')}>
                {elem}
            </BolmateTooltip>
        );
    }

    return (
        <div className='search-rankings'>
            <InfoBanner className='feature-info' persistDismiss={false}>
                {t(
                    'searchRankings.banner.text',
                    "De getoonde rankings kunnen soms afwijken van de getoonde positie op Bol.\n\nBol test verschillende productpagina's middels A/B testen, de getoonde rankings is altijd gebaseerd op de reguliere weergave van Bol."
                )}
            </InfoBanner>

            {/* KPI Tiles */}
            <div className='search-rankings-kpi-grid'>
                {kpis.map((kpi, idx) => {
                    return <BolmateStatCard data={kpi} key={idx} />;
                })}
            </div>

            <div className='positions-row'>
                {/* Position Filters */}
                <PositionFilterBar
                    positionCounts={stats.position_counts}
                    prevPositionCounts={prevStats.position_counts}
                    activeFilter={activePositionFilter}
                    onFilterChange={onPositionFilterChange}
                />

                {/* Account limit */}
                {renderLimitIndicator()}
            </div>

            {/* Control Bar */}
            <SearchRankingsControlBar
                onSearchChange={onSearchChange}
                rankingMode={rankingMode}
                onRankingModeChange={onRankingModeChange}
                selectedCountries={selectedCountries}
                onCountryChange={onCountryChange}
                selectedAccountIDs={selectedAccountIDs}
                storeItems={storeItems}
                onStoreChange={onStoreChange}
                onExportExcel={onExportExcel}
                visibleColumnFields={visibleColumnFields}
                allColumnItems={allColumnItems}
                onColumnsChange={onColumnsChange}
                onImportClick={onImportClick}
                manualKeywordsCount={manualKeywordsCount}
                manualKeywordsLimit={manualKeywordsLimit}
                onCloseAccountItems={onCloseAccountItems}
                accountID={accountId}
            />

            {/* Products Table */}
            <SearchRankingsTable
                products={products}
                loading={loadingProducts}
                rankingMode={rankingMode}
                expandedProductId={expandedProductId}
                onToggleExpand={onToggleExpand}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
                page={page}
                limit={limit}
                totalRows={totalRows}
                onPageChange={onPageChange}
                onLimitChange={props.onLimitChange}
                accountId={accountId}
                accountIds={selectedAccountIDs}
                countryCodes={selectedCountries}
                onImportClick={onImportClick}
                onAddKeyword={onAddKeyword}
                columns={columns}
                onKeywordsChanged={onKeywordsChanged}
                manualKeywordCount={manualKeywordsCount}
                manualKeywordLimit={manualKeywordsLimit}
                hasModule={hasModule}
            />

            {renderAPIBanner()}

            <ImportKeywordsDialog
                active={importDialogActive}
                onClose={onImportClose}
                accountId={accountId}
                onSuccess={onKeywordsChanged}
            />
        </div>
    );
}
